// ViewWheelLoadingShell — View Wheel's loading state (route-chunk download AND
// the wheel-data fetch). T13.1 (Kelly 2026-10-07, re-measured live on
// web-stg.iyuga.net main.d15367ee.js, 2026-10-10): opening a wheel from the
// Gallery showed a BLANK WHITE PAGE with only the spinner for 1.0s / 2.0s / 6.8s
// (3/3 trials, per-rAF DOM timeline: zero <header> in the DOM while `loading`).
// The page tree gates WheelHeader on `!loading`, and the fullpage brand loader
// is a white-scrim overlay with nothing behind it, so for the whole
// GET /api/v2/wheel/<id>/ round trip the page had no chrome at all.
//
// This keeps the SAME header bar (WheelHeader's own HeaderMainDiv/LeftContainer/
// Icon styles = identical height and logo position) above the SAME viewport-
// centred fullpage brand loader, so the page never reads as blank and the logo
// does not jump when the real WheelHeader takes over. Only the logo is shown —
// the wheel name and the owner/guest action icons depend on the wheel payload
// and would flash wrong values (e.g. "Untitled Wheel", guest Clone icons on the
// owner's own wheel) if rendered before it arrives.
//
// Phone portrait renders the loader alone, exactly as before: MobileView owns a
// different header row, so a desktop bar there would itself be a swap.
//
// Drop-in for CustomLoader (`skipEntry`, defaults: label "Loading…", disc on),
// so ViewWheel's `loading ? <Loader skipEntry /> : (...)` gates are unchanged.
//
// T13.1 re-audit (audit-10131014-box-other-1; re-measured stgfix-10131058 on
// web-stg.iyuga.net main.061a3dfa.js, 1440x900, 15 fps video): the page still
// read as a blank white page with a spinner, TWICE. A <header> was in the DOM
// the whole time, but in pixels:
//  1. route chunk + wheel GET: the bar held only the 42px logo, and the white
//     scrim covered everything else (header band 0.4% non-white);
//  2. the portrait hold (`loading` false, `holdForPortrait` true): the real
//     WheelHeader had mounted UNDER the fullpage scrim (z 1300), so it was
//     washed to 30%. This shell's own logo bar also sat below it as a second
//     header, and the loader kept spinning. That read as a second blank page.
// Fix: the scrim now starts BELOW the header bar (both phases, same offset, so
// the wheel does not move between them). Phase 1 draws neutral placeholders in
// the bar (title + icon slots, no payload-dependent values). In phase 2
// (`headerMounted`) the shell draws no bar of its own, so the real header
// shows at full strength above the loader.
//
// T13.1 audit (audit-10131252-box-other-1; re-measured stgfix-10131314 on
// web-stg.iyuga.net main.d9c3cd8d.js, 390x844, gallery dblclick, 3/3 trials):
// the fix above was desktop-only. In phone portrait the shell still returned
// the loader alone, so the DOM held no <header> for ~0.5-0.7 s per open (and
// 4.7 s with a 3 s API delay): a white page with only the ring loader. Phone
// portrait now gets its own bar with MobileView's two-row geometry
// (SearchHeader row 10svh + icon row 8svh): the logo plus neutral
// placeholders, and the scrim starts below it (.vwls-below-mobile-header).
import React from "react";
import { useNavigate } from "react-router-dom";
import BrandWheelLoader from "../../components/BrandWheelLoader";
import useScreenSize from "../../hooks/useScreenSize";
import { ASSETS } from "../../utils/config";
import * as HeaderStyled from "../../components/WheelHeader/styles";
import * as MobileStyled from "./MobileView/styles";
import "./ViewWheelLoadingShell.css";

// Above the fullpage loader's fixed scrim (.bwl-fullpage z-index 1300).
const HEADER_Z_INDEX = 1301;
// BrandWheelLoader sizes itself off its PARENT box. Inside the pre-fix ViewWheel
// tree that parent measured 0px tall, so it fell back to the fullpage default
// (160px, measured live). As the header's sibling the parent is the short route
// container and the wheel shrank to a dot (measured on the first PR build) —
// pin the size the live page has always shown on non-phone viewports.
const DESKTOP_LOADER_PX = 160;
// Starts the loader scrim at HeaderMainDiv's height (8vh, min 60px); see .css.
export const BELOW_HEADER_CLASS = "vwls-below-header";
// Phone portrait: starts the scrim below MobileView's two header rows (18svh).
export const BELOW_MOBILE_HEADER_CLASS = "vwls-below-mobile-header";
// Phone loader size measured live before the bar existed (150px ring = 160px
// box). With the bar as a sibling the loader measured its short route parent
// and shrank to the 48px clamp floor on the first PR build, so pin it.
const PHONE_LOADER_PX = 160;
// MobileView's icon row (edit/info/AD/heart/share/chat).
const MOBILE_ICON_SLOTS = 6;
// Placeholder slots for the 7 action icons + the avatar ring on the right.
const ICON_SLOTS = 7;
const SLOT = { background: "#e3e3ec", borderRadius: 999, flex: "0 0 auto" };

const ViewWheelLoadingShell = ({
  skipEntry = false,
  label = "Loading…",
  disc = true,
  headerMounted = false,
} = {}) => {
  const nav = useNavigate();
  const { isMobilePortrait } = useScreenSize();
  const loader = (size, className) => (
    <BrandWheelLoader
      variant="fullpage"
      label={label}
      skipEntry={skipEntry}
      disc={disc}
      size={size}
      className={className}
    />
  );
  if (headerMounted && !isMobilePortrait) {
    return loader(DESKTOP_LOADER_PX, BELOW_HEADER_CLASS);
  }

  const goDiscover = () => nav("/discover");
  const logoProps = {
    onClick: goDiscover,
    role: "button",
    tabIndex: 0,
    "aria-label": "iYuga home — go to Discover",
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        goDiscover();
      }
    },
  };

  if (isMobilePortrait) {
    return (
      <>
        <header
          data-testid="view-wheel-loading-header-mobile"
          style={{ position: "relative", zIndex: HEADER_Z_INDEX }}
        >
          <MobileStyled.Header>
            <div className="vwls-mobile-search-row">
              <img
                src={ASSETS.iyugaLogo}
                alt="Iyuga logo"
                width={40}
                height={40}
                {...logoProps}
              />
              <div
                data-testid="view-wheel-loading-search"
                aria-hidden="true"
                style={{ ...SLOT, borderRadius: 8, height: 36 }}
              />
              <div aria-hidden="true" style={{ ...SLOT, width: 42, height: 42 }} />
            </div>
            <MobileStyled.IconsHeader
              data-testid="view-wheel-loading-actions-mobile"
              aria-hidden="true"
            >
              {Array.from({ length: MOBILE_ICON_SLOTS }, (_, i) => (
                <div key={i} className="vwls-mobile-icon-slot">
                  <div style={{ ...SLOT, borderRadius: 4, width: 24, height: 24 }} />
                </div>
              ))}
            </MobileStyled.IconsHeader>
          </MobileStyled.Header>
        </header>
        {loader(PHONE_LOADER_PX, BELOW_MOBILE_HEADER_CLASS)}
      </>
    );
  }

  return (
    <>
      <header
        data-testid="view-wheel-loading-header"
        style={{ position: "relative", zIndex: HEADER_Z_INDEX }}
      >
        <HeaderStyled.HeaderMainDiv>
          <HeaderStyled.LeftContainer>
            <HeaderStyled.Icon src={ASSETS.iyugaBetaLogo} alt="Iyuga logo" {...logoProps} />
          </HeaderStyled.LeftContainer>
          <div
            data-testid="view-wheel-loading-title"
            aria-hidden="true"
            style={{ ...SLOT, justifySelf: "center", width: 200, height: 18 }}
          />
          <div
            data-testid="view-wheel-loading-actions"
            aria-hidden="true"
            style={{ display: "flex", alignItems: "center", justifySelf: "end", gap: 22 }}
          >
            {Array.from({ length: ICON_SLOTS }, (_, i) => (
              <div key={i} style={{ ...SLOT, width: 26, height: 26 }} />
            ))}
            <div style={{ ...SLOT, width: 42, height: 42 }} />
          </div>
        </HeaderStyled.HeaderMainDiv>
      </header>
      {loader(DESKTOP_LOADER_PX, BELOW_HEADER_CLASS)}
    </>
  );
};

export default ViewWheelLoadingShell;
